import { useState, useMemo, useEffect, useRef } from "react"
import { Box, Button, Card, CardContent, Divider, Stack, TextField, Typography } from "@mui/material"
import { setFromEvent } from "lib/setFromEvent"
import { PageBreadcrumbContainer } from "lib/page-breadcrumb-container"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { busyWhile } from "lib/@components/busy/busy-while"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { navigate } from "lib/routes/navigate"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { useParams } from "react-router"
import { getClientGroups } from "routes/schedule/my-schedules/my-custom-schedules/controller/get-client-trees"
import { rateCards } from "routes/rate-cards/controller/rate-cards"
import { ruleSets } from "routes/rule-sets/controller/rule-sets"
import { createScenario } from "routes/scenarios/controller/create-scenario"
import { updateScenario } from "routes/scenarios/controller/update-scenario"
import { scenario as scenarioApi, scenarios } from "routes/scenarios/controller/scenarios"
import { If } from "lib/@components/switch"
import { useDuplicateNameCheck } from "../use-unique-name-validation"
import { RegimeSection } from "./regime-section"
import { RateCardSection } from "./rate-card-section"
import { RateCardWarnings } from "./rate-card-warnings"
import { RuleSetSection } from "./rule-set-section"
import { RegimePreviewCard } from "./regime-preview-card"
import { SelectionSummaryCard } from "./selection-summary-card"
import { GetStartedFab } from "../../get-started-fab"

const parentCrumbs = [{ label: "Optimiser", to: "/app/optimiser" }]

export function CreateScenarioPage() {
    const { id } = useParams()
    const isEditing = !!id
    const existingScenario = scenarioApi.useResultsOnce(isEditing ? { id } : null)

    const [scenarioName, setScenarioName] = useSearchParam("name", isEditing ? "" : "Scenario 1")
    const [regimeIdParam, setRegimeIdParam] = useSearchParam("regimeId", "")
    const [rateCardIdParam, setRateCardIdParam] = useSearchParam("rateCardId", "")
    const [ruleSetIdParam, setRuleSetIdParam] = useSearchParam("ruleSetId", "")
    const nameInputRef = useRef(null)
    const [selectedRegime, setSelectedRegime] = useState(null)
    const [selectedRateCard, setSelectedRateCard] = useState(null)
    const [selectedRuleSets, setSelectedRuleSets] = useState([])
    const [rateCardExpanded, setRateCardExpanded] = useState(true)
    const [initialized, setInitialized] = useState(false)

    const clientGroups = getClientGroups.useResults()
    const regimes = useMemo(getRegimes, [clientGroups])

    const rateCardResult = rateCards.useResults({ skip: 0, take: 100, status: "active" })
    const rateCardItems = useMemo(getRateCardItems, [rateCardResult])

    const ruleSetResult = ruleSets.useResults({ skip: 0, take: 100, status: "active" })
    const ruleSetItems = ruleSetResult?.items ?? []
    const selectedRuleSetIds = useMemo(getSelectedRuleSetIds, [selectedRuleSets])

    const scenarioListResult = scenarios.useResults({ skip: 0, take: 1000 })
    const isDuplicateName = useDuplicateNameCheck(scenarioListResult?.items, { excludeId: id })
    const nameIsDuplicate = isDuplicateName(scenarioName)
    // Computed outside JSX: the repo forbids ternaries inside JSX.
    const duplicateNameError = nameIsDuplicate
        ? `A scenario with the name "${scenarioName.trim()}" already exists`
        : undefined

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(initFromExisting, [existingScenario, regimes.length, rateCardItems.length, ruleSetItems.length])

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(focusNameOnCreate, [])

    useEffect(hydrateSelectedRegime, [regimes, selectedRegime, regimeIdParam])

    useEffect(hydrateSelectedRateCard, [rateCardItems, selectedRateCard, rateCardIdParam])

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(hydrateSelectedRuleSets, [ruleSetItems, selectedRuleSets, ruleSetIdParam])

    const canCreate = !!scenarioName.trim() && !!selectedRegime && !!selectedRateCard && !nameIsDuplicate

    const pageTitle = isEditing ? "Edit Scenario" : "Create Scenario"

    if (isEditing && existingScenario === undefined) return <LoadingScreen description="Loading scenario..." />

    if (isEditing && existingScenario === null) {
        return (
            <PageBreadcrumbContainer title="Scenario Not Found" parentCrumbs={parentCrumbs} sx={{ height: 1 }}>
                <Stack alignItems="center" justifyContent="center" sx={{ flex: 1, p: 3 }}>
                    <Typography variant="h6" color="text.secondary">
                        This scenario could not be found.
                    </Typography>
                </Stack>
            </PageBreadcrumbContainer>
        )
    }

    return (
        <PageBreadcrumbContainer title={pageTitle} parentCrumbs={parentCrumbs} sx={{ height: 1 }}>
            <Stack
                data-cy="create-scenario-page"
                direction="row"
                spacing={3}
                sx={{ px: 3, pt: 2, flex: 1, alignItems: "flex-start" }}
            >
                <Card sx={{ flex: 1, maxWidth: 780 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Stack spacing={3}>
                            <Stack spacing={1.5}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    Scenario Name
                                </Typography>
                                <TextField
                                    data-cy="scenario-name"
                                    inputRef={nameInputRef}
                                    value={scenarioName}
                                    onChange={setFromEvent(setScenarioName)}
                                    placeholder="Enter scenario name..."
                                    fullWidth
                                    error={nameIsDuplicate}
                                    helperText={duplicateNameError}
                                />
                            </Stack>
                            <RegimeSection
                                regimes={regimes}
                                selectedRegime={selectedRegime}
                                onSelect={selectRegime}
                                disabled={isEditing}
                            />
                            <Divider sx={{ borderStyle: "dashed" }} />
                            <RateCardSection
                                rateCardItems={rateCardItems}
                                selectedRateCard={selectedRateCard}
                                selectedRegime={selectedRegime}
                                onSelect={selectRateCard}
                                expanded={rateCardExpanded}
                                onToggleExpand={toggleRateCardExpand}
                            />
                            <If value={selectedRateCard && !isSyntheticRateCard(selectedRateCard)}>
                                <RateCardWarnings rateCard={selectedRateCard} />
                            </If>
                            <RuleSetSection
                                ruleSetItems={ruleSetItems}
                                selectedRuleSets={selectedRuleSets}
                                onSelect={selectRuleSets}
                            />
                        </Stack>
                    </CardContent>
                </Card>
                <Box sx={{ width: 320, flexShrink: 0, position: "sticky", top: 16, alignSelf: "flex-start" }}>
                    <Stack spacing={2}>
                        <RegimePreviewCard regime={selectedRegime} />
                        <If value={selectedRateCard || selectedRuleSets.length > 0}>
                            <SelectionSummaryCard rateCard={selectedRateCard} ruleSets={selectedRuleSets} />
                        </If>
                    </Stack>
                    <Button
                        data-cy="create-scenario-btn"
                        variant="contained"
                        fullWidth
                        disabled={!canCreate}
                        onClick={handleCreate}
                        sx={{ mt: 2 }}
                    >
                        {buttonLabel()}
                    </Button>
                </Box>
            </Stack>
            <GetStartedFab />
        </PageBreadcrumbContainer>
    )

    function buttonLabel() {
        return isEditing ? "Save Scenario" : "Create Scenario"
    }

    function focusNameOnCreate() {
        if (isEditing) return
        const input = nameInputRef.current
        if (!input) return
        input.focus()
        input.select()
    }

    function initFromExisting() {
        if (initialized || !isEditing || !existingScenario) return

        setScenarioName(existingScenario.name || "")

        const regime = regimes.find(matchesExistingRegimeId)
        if (regime) {
            setSelectedRegime(regime)
        } else if (existingScenario.regimeId) {
            setSelectedRegime({
                id: existingScenario.regimeId,
                label: existingScenario.regimeName || existingScenario.regimeId,
            })
        }

        const card = rateCardItems.find(matchesExistingRateCardId)
        if (card) {
            setSelectedRateCard(card)
        } else if (existingScenario.rateCardId) {
            setSelectedRateCard({
                id: existingScenario.rateCardId,
                name: existingScenario.rateCardName || existingScenario.rateCardId,
            })
        }

        const existingRuleSets = resolveExistingRuleSets()
        if (existingRuleSets.length) {
            setSelectedRuleSets(existingRuleSets)
        }

        setInitialized(true)
    }

    function hydrateSelectedRegime() {
        if (!selectedRegime && regimeIdParam && regimes.length) {
            const fromParam = regimes.find(matchesRegimeIdParam)
            if (fromParam) {
                setSelectedRegime(fromParam)
                return
            }
        }
        if (!selectedRegime || selectedRegime.facilities || !regimes.length) return
        const fullRegime = regimes.find(matchesSelectedRegimeId)
        if (fullRegime) {
            setSelectedRegime(fullRegime)
        }
    }

    function hydrateSelectedRateCard() {
        if (!selectedRateCard && rateCardIdParam && rateCardItems.length) {
            const fromParam = rateCardItems.find(matchesRateCardIdParam)
            if (fromParam) {
                setSelectedRateCard(fromParam)
                return
            }
        }
        if (!selectedRateCard || !rateCardItems.length) return
        const fullCard = rateCardItems.find(matchesSelectedRateCardId)
        if (fullCard && fullCard !== selectedRateCard) {
            setSelectedRateCard(fullCard)
        }
    }

    function hydrateSelectedRuleSets() {
        if (!selectedRuleSets.length && ruleSetIdParam && ruleSetItems.length) {
            const fromParam = ruleSetItems.find(matchesRuleSetIdParam)
            if (fromParam) {
                setSelectedRuleSets([fromParam])
            }
            return
        }
        if (!selectedRuleSets.length || !ruleSetItems.length) return
        const fullRuleSets = selectedRuleSets.map(resolveRuleSetItem)
        if (fullRuleSets.some(isDifferentRuleSet)) {
            setSelectedRuleSets(fullRuleSets)
        }
    }

    function selectRegime(regime) {
        setSelectedRegime(regime)
        setRegimeIdParam(regime?.id ?? "")
    }

    function selectRateCard(card) {
        setSelectedRateCard(card)
        setRateCardIdParam(card?.id ?? "")
    }

    function selectRuleSets(ruleSets) {
        setSelectedRuleSets(ruleSets)
        setRuleSetIdParam(ruleSets[0]?.id ?? "")
    }

    function isSyntheticRateCard(card) {
        return !card.lines
    }

    function getRateCardItems() {
        return rateCardResult?.items ?? []
    }

    function getRegimes() {
        return (clientGroups ?? [])
            .flatMap((g) => {
                const entries = []
                if (g.hasLive) {
                    entries.push({
                        id: `live|${g._id}`,
                        label: g.name,
                        _id: g._id,
                        facilities: g.facilities,
                        scheduleCount: g.scheduleCount,
                    })
                }
                if (g.hasReady) {
                    entries.push({
                        id: `ready|${g._id}`,
                        label: g.name,
                        _id: g._id,
                        facilities: g.facilities,
                        scheduleCount: g.scheduleCount,
                    })
                }
                return entries
            })
            .sort((a, b) => a.label.localeCompare(b.label))
    }

    function toggleRateCardExpand() {
        setRateCardExpanded(!rateCardExpanded)
    }

    async function handleCreate() {
        if (!selectedRegime || !selectedRateCard) return
        const regimeName = selectedRegime.label.replaceAll("Live:", "").replaceAll("Ready:", "")

        const name = scenarioName.trim() || regimeName

        if (isEditing) {
            const ok = await busyWhile(
                () =>
                    updateScenario({
                        id,
                        name,
                        rateCardId: selectedRateCard.id,
                        ruleSetId: selectedRuleSetIds[0] || null,
                        ruleSetIds: selectedRuleSetIds,
                    }),
                "Saving scenario..."
            )
            if (!ok) return
            successSnackbar("Scenario updated")
            navigate(`/app/optimiser/scenarios/${id}`)
            return
        }
        const result = await busyWhile(
            () =>
                createScenario({
                    name,
                    regimeId: selectedRegime.id,
                    rateCardId: selectedRateCard.id,
                    ruleSetId: selectedRuleSetIds[0] || null,
                    ruleSetIds: selectedRuleSetIds,
                }),
            "Creating scenario..."
        )
        if (result?.id) {
            successSnackbar(`Scenario "${name}" created`)
            navigate(`/app/optimiser/scenarios/${result.id}`)
        }
    }

    function getSelectedRuleSetIds() {
        return selectedRuleSets.map(toRuleSetId).filter(Boolean)
    }

    function toRuleSetId(ruleSet) {
        return ruleSet.id
    }

    function resolveExistingRuleSets() {
        return getExistingRuleSetIds().map(toExistingRuleSet).filter(Boolean)
    }

    function getExistingRuleSetIds() {
        const firstVariant = existingScenario.variants?.[0]
        if (firstVariant?.ruleSetIds?.length) return firstVariant.ruleSetIds
        if (firstVariant?.ruleSetId) return [firstVariant.ruleSetId]
        if (existingScenario.ruleSetId) return [existingScenario.ruleSetId]
        return []
    }

    function toExistingRuleSet(ruleSetId, index) {
        const found = ruleSetItems.find(matchesRuleSetId)
        if (found) return found
        return {
            id: ruleSetId,
            name: getExistingRuleSetName(index, ruleSetId),
        }

        function matchesRuleSetId(ruleSet) {
            return ruleSet.id === ruleSetId
        }
    }

    function getExistingRuleSetName(index, ruleSetId) {
        const firstVariant = existingScenario.variants?.[0]
        return firstVariant?.ruleSetNames?.[index] || existingScenario.ruleSetName || ruleSetId
    }

    function resolveRuleSetItem(ruleSet) {
        return ruleSetItems.find((item) => item.id === ruleSet.id) ?? ruleSet
    }

    function isDifferentRuleSet(ruleSet, index) {
        return ruleSet !== selectedRuleSets[index]
    }

    function matchesExistingRegimeId(regime) {
        return regime.id === existingScenario.regimeId
    }

    function matchesExistingRateCardId(card) {
        return card.id === existingScenario.rateCardId
    }

    function matchesRegimeIdParam(regime) {
        return regime.id === regimeIdParam
    }

    function matchesSelectedRegimeId(regime) {
        return regime.id === selectedRegime.id
    }

    function matchesRateCardIdParam(card) {
        return card.id === rateCardIdParam
    }

    function matchesSelectedRateCardId(card) {
        return card.id === selectedRateCard.id
    }

    function matchesRuleSetIdParam(ruleSet) {
        return ruleSet.id === ruleSetIdParam
    }
}
